import { useEffect, useState, useCallback } from "react";
import { Layout } from "@/components/Layout";
import { toast } from "sonner";
import {
  Plus, Link2, Copy, MessageCircle, Mail, DownloadCloud, Trash2, Loader2,
  ClipboardList, CheckSquare, Users, Image as ImageIcon, Video as VideoIcon,
} from "lucide-react";
import { crearFormularioLibre, listarFormulariosLibres, eliminarFormulario, formularioPdfUrl } from "@/lib/prevelis";

const ESTADO = {
  enviado: { label: "Enviado", cls: "bg-blue-50 text-blue-700" },
  abierto: { label: "Abierto", cls: "bg-blue-50 text-blue-700" },
  en_progreso: { label: "En progreso", cls: "bg-amber-50 text-amber-700" },
  completado: { label: "Completado", cls: "bg-emerald-50 text-emerald-700" },
};

export default function Formularios() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    listarFormulariosLibres().then((f) => { setForms(f); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const publicLink = (token) => `${window.location.origin}/f/${token}`;

  const crear = async () => {
    setBusy(true);
    try {
      const { token } = await crearFormularioLibre("");
      await load();
      try { await navigator.clipboard.writeText(publicLink(token)); toast.success("Enlace creado y copiado al portapapeles"); }
      catch { toast.success("Enlace creado"); }
    } catch { toast.error("No se pudo crear el formulario"); }
    finally { setBusy(false); }
  };

  const copiar = async (token) => {
    try { await navigator.clipboard.writeText(publicLink(token)); toast.success("Enlace copiado"); }
    catch { toast.error("No se pudo copiar"); }
  };
  const msgWa = (token) => encodeURIComponent(`Hola, para preparar la evaluación de prevención de riesgos laborales, por favor cumplimente este breve formulario: ${publicLink(token)}`);
  const cuerpoMail = (token) => encodeURIComponent(`Buenos días,\n\nPara preparar la evaluación de prevención de riesgos laborales, le agradeceríamos que cumplimente este formulario:\n\n${publicLink(token)}\n\nGracias por su colaboración.`);

  const borrar = async (id) => {
    try { await eliminarFormulario(id); await load(); toast.success("Formulario eliminado"); }
    catch { toast.error("No se pudo eliminar"); }
  };

  return (
    <Layout title="Formularios" subtitle="Envía un enlace al cliente para que aporte sus datos. Independiente de empresas y planes."
      actions={<button onClick={crear} disabled={busy} data-testid="form-crear-btn" className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-blue-700 text-white text-sm font-medium hover:bg-blue-800 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Nuevo formulario</button>}>
      <div className="max-w-4xl">
        <div className="mb-6 rounded-xl bg-blue-50 border border-blue-100 px-4 py-3 text-sm text-blue-800 flex items-start gap-2">
          <ClipboardList className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Crea un enlace, envíaselo al cliente y este rellenará datos de empresa, trabajadores y el checklist. Sus respuestas <b>no se conectan con ningún expediente</b>; tú decides si te sirven. El cliente no accede a la aplicación.</span>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
        ) : forms.length === 0 ? (
          <div className="text-center py-16 text-slate-400" data-testid="form-vacio">
            <ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">Aún no has creado ningún formulario.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="form-lista">
            {forms.map((f) => {
              const est = ESTADO[f.estado] || ESTADO.enviado;
              return (
                <div key={f.id} data-testid={`form-item-${f.id}`} className="bg-white rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-800">{f.empresa_nombre || f.nombre}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${est.cls}`} data-testid={`form-estado-${f.id}`}>{est.label}</span>
                    <span className="ml-auto flex items-center gap-3 text-xs text-slate-400">
                      <span className="inline-flex items-center gap-1"><CheckSquare className="w-3.5 h-3.5" /> {f.num_respuestas}</span>
                      <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {f.num_trabajadores}</span>
                      <span className="inline-flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> {f.num_fotos}</span>
                      {f.tiene_video && <span className="inline-flex items-center gap-1"><VideoIcon className="w-3.5 h-3.5" /> vídeo</span>}
                    </span>
                  </div>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <code className="text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1 text-slate-500 truncate max-w-[260px]">{publicLink(f.token)}</code>
                    <button onClick={() => copiar(f.token)} data-testid={`form-copiar-${f.id}`} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-slate-300 text-xs font-medium text-slate-600 hover:bg-slate-100"><Copy className="w-3.5 h-3.5" /> Copiar</button>
                    <a href={`https://wa.me/?text=${msgWa(f.token)}`} target="_blank" rel="noreferrer" data-testid={`form-whatsapp-${f.id}`} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-emerald-300 text-xs font-medium text-emerald-700 hover:bg-emerald-50"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</a>
                    <a href={`mailto:?subject=${encodeURIComponent("Formulario de prevención de riesgos laborales")}&body=${cuerpoMail(f.token)}`} data-testid={`form-email-${f.id}`} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-slate-300 text-xs font-medium text-slate-600 hover:bg-slate-100"><Mail className="w-3.5 h-3.5" /> Email</a>
                    <a href={formularioPdfUrl(f.id)} target="_blank" rel="noreferrer" data-testid={`form-pdf-${f.id}`} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-slate-300 text-xs font-medium text-slate-600 hover:bg-slate-100"><DownloadCloud className="w-3.5 h-3.5" /> PDF</a>
                    <button onClick={() => borrar(f.id)} data-testid={`form-borrar-${f.id}`} className="ml-auto w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
